import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import queryString from 'query-string';
import React, { useContext, useEffect } from 'react';
import { __RouterContext } from 'react-router';
import { useUpdateEffect } from 'react-use';
import { SEARCH } from '../../../routes';
import { appContext } from '../../../state/appState';
import { modalContext } from '../../../state/modalState';
import { updateUrlEvent, updateUrlSubject } from '../../../state/rxjs';
import { searchContext } from '../../../state/search/searchState';
import { Button } from '../button/Button';
import { IconDashboard, IconDeleteSweep, IconFilter, IconSearch } from '../icons';
import { ChipsInput } from './ChipsInput';
import './Searchbar.css';
import { Link } from 'react-router-dom';
import { useIsSmallViewport } from 'utils/hooks';

export const Searchbar = observer(() => {
  const searchState = useContext(searchContext);
  const routerState = useContext(__RouterContext);
  const appState = useContext(appContext);
  const modalState = useContext(modalContext);

  const updateUrl = () => {
    const totalQuery = searchState.generateUrlFromChips();
    const oldUrl = routerState.location.search;
    const newUrl = '?' + totalQuery;
    if (oldUrl === newUrl) {
      // nothing
    } else if (totalQuery.length === 0) {
      routerState.history.push(SEARCH);
    } else {
      routerState.history.push(SEARCH + newUrl);
    }
  };

  const parseLocationAndSearch = () => {
    if (routerState.location.search.length > 0) {
      const queries = queryString.parse(routerState.location.search);
      searchState
        .getChipsFromQuery(queries)
        .then(searchState.prepareFilterChips)
        .then(searchState.runSearch)
        .then(() => {
          updateUrlSubject.next();
        });
    } else {
      searchState.clearSearch();
    }
  };

  useUpdateEffect(() => {
    // for handling back/forward navigation
    const lastUrl = searchState.generateUrlFromChips();
    if (
      '?' + lastUrl === routerState.location.search ||
      lastUrl === routerState.location.search
    ) {
      return;
    }
    parseLocationAndSearch();
  }, [routerState.location.search]);

  useEffect(() => {
    const subscription = updateUrlEvent.subscribe(() => {
      updateUrl();
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [routerState.location.search]);

  useEffect(() => {
    runInAction(() => {
      searchState.filterOptionHolds = {
        Yes: 'yes',
        No: 'no',
        Client: 'client',
        [window.COMPANY_LABEL]: 'internal',
      };
    });
    searchState.getContracts();
    if (searchState.caseView && searchState.searchedCasesState.hasSearched) {
      return;
    }
    if (!searchState.caseView && searchState.searchedDebtorsState.hasSearched) {
      return;
    }
    if (!searchState.searching) {
      parseLocationAndSearch();
    }
  }, []);

  const openFilter = () => {
    modalState.toggleModal('filters', true);
  };

  const submitSearch = () => {
    searchState.submitSearch();
  };

  const clearSearch = () => {
    searchState.clearSearch();
    updateUrl();
  };

  let filterBtnCaption = routerState.location.pathname === '/'
    ? 'Advanced Search'
    : 'Filter';

  
  const MAX_WIDTH = 400;
  const isSmallViewport = useIsSmallViewport(MAX_WIDTH);

  return (
    <>
      {routerState.location.pathname != SEARCH &&
        <div className="searchbar-label-wrapper">
          <label className="quick-search-label" htmlFor="quick-search">
            {isSmallViewport
              ? 'Search by Reference, VRM, Name, or Postcode. Minimum of 2 characters required.'
              : 'Search. Minimum of 2 characters required.'}
          </label>
        </div>
      }
      <div className={'Searchbar' + (searchState.searching ? ' blocked' : '')} role='search'>
        <ChipsInput
          submitSearch={submitSearch} updateUrl={updateUrl}
          inputId='quick-search'
          placeholderText={isSmallViewport ? 'Search' : 'Reference, VRM, Name, or Postcode'}
          ariaLabel={routerState.location.pathname == SEARCH ? 'Search by Reference, VRM, Name, or Postcode. Minimum of 2 characters required.' : undefined}
        />
        <div className='searchbar-buttons'>
          <Button
            loading={searchState.searching}
            onClick={submitSearch}
            className='nav-link search'
            aria-label='Submit Search'
          >
            <IconSearch />
          </Button>
          <Button onClick={openFilter} className='nav-link filter' aria-label={filterBtnCaption}>
            <IconFilter />
            <span>
              {filterBtnCaption}
            </span>
          </Button>
          <Button onClick={clearSearch} className='nav-link clear' aria-label='Clear Search'>
            <IconDeleteSweep />
            <span>Clear Search</span>
          </Button>
        </div>
      </div>
    </>
  );
});
